import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Target, LayoutDashboard, ClipboardCheck, MessagesSquare, MessageSquarePlus, Loader2, Plus, X, Trash2, Star, Award, GraduationCap, TrendingUp, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import DatePicker from "../../components/os/DatePicker";
import { EmployeePicker } from "../../components/os/EmployeePicker";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const LEADERSHIP = ["admin", "ceo", "director"];

function Modal({ title, onClose, children, testid, wide }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        className={`card-surface w-full ${wide ? "max-w-xl" : "max-w-md"} max-h-[90vh] overflow-y-auto p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{title}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

function ProgressBar({ value }) {
  return (
    <div className="h-2 rounded-full bg-navy/5 overflow-hidden">
      <div className="h-full bg-gradient-violet" style={{ width: `${Math.min(100, value)}%` }} />
    </div>
  );
}

/* ============ PANEL ============ */
function DashboardTab() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/performance/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const kpis = [
    { label: t("os.performance.objectivesTotal"), value: d.objectives_total }, { label: t("os.performance.avgProgress"), value: `${d.objectives_avg_progress}%` },
    { label: t("os.performance.reviewsDraft"), value: d.reviews_draft }, { label: t("os.performance.reviewsSent"), value: d.reviews_sent },
    { label: t("os.performance.feedbackCount"), value: d.feedback_count }, { label: t("os.performance.oneononesCount"), value: d.oneonones },
  ];
  return (
    <div className="space-y-8" data-testid="perf-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((k, i) => (
          <div key={k.label} className="card-surface p-5" data-testid={`perf-dash-kpi-${i}`}>
            <div className="overline text-navy/40">{k.label}</div>
            <div className="mt-3 text-3xl font-medium tracking-tight">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="card-surface p-6">
        <div className="overline text-navy/40 mb-4">{t("os.performance.byStatus")}</div>
        {d.objectives_by_status.map((s) => (
          <div key={s.status} className="flex items-center justify-between py-1.5 border-b border-navy/5 last:border-0">
            <span className="text-sm text-navy/70">{t(`os.performance.objStatus.${s.status}`)}</span><span className="badge badge-violet">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ OBJETIVOS / OKRs ============ */
function ObjectiveDialog({ meta, isLeader, onClose, onSaved }) {
  const { t } = useI18n();
  const [f, setF] = useState({ title: "", description: "", period: meta.current_period, user_id: "", key_results: [{ text: "", target: "", current: 0 }] });
  const [saving, setSaving] = useState(false);
  const setKr = (i, k, v) => { const n = [...f.key_results]; n[i][k] = v; setF({ ...f, key_results: n }); };
  const submit = async () => {
    if (!f.title.trim()) return toast.error(t("os.performance.objTitle"));
    setSaving(true);
    try {
      const krs = f.key_results.filter((k) => k.text.trim()).map((k) => ({ text: k.text, target: k.target === "" ? null : Number(k.target), current: Number(k.current) || 0, done: false }));
      await api.post("/performance/objectives", { title: f.title, description: f.description, period: f.period, user_id: isLeader && f.user_id ? f.user_id : null, key_results: krs });
      toast.success(t("os.performance.objectiveCreated")); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Modal title={t("os.performance.newObjective")} onClose={onClose} testid="perf-objective-dialog" wide>
      <div className="space-y-3">
        <input className="field" placeholder={t("os.performance.objTitle")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="perf-obj-title" />
        <textarea className="field" rows={2} placeholder={t("os.performance.objDesc")} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <select className="field" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })}>
            {meta.periods.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {isLeader && (
            <EmployeePicker testid="perf-obj-assign" value={f.user_id || null} scope="managed"
              placeholder={`${t("os.performance.assignTo")} (yo)`}
              onChange={(emp) => setF({ ...f, user_id: emp ? emp.id : "" })} />
          )}
        </div>
        <div className="pt-1">
          <div className="text-sm font-medium mb-1.5">{t("os.performance.keyResults")}</div>
          {f.key_results.map((k, i) => (
            <div key={i} className="grid grid-cols-6 gap-1.5 mb-1.5">
              <input className="field-sm col-span-3" placeholder={t("os.performance.krText")} value={k.text} onChange={(e) => setKr(i, "text", e.target.value)} data-testid={`perf-kr-text-${i}`} />
              <input className="field-sm" type="number" placeholder={t("os.performance.krTarget")} value={k.target} onChange={(e) => setKr(i, "target", e.target.value)} />
              <input className="field-sm" type="number" placeholder={t("os.performance.krCurrent")} value={k.current} onChange={(e) => setKr(i, "current", e.target.value)} />
              <button onClick={() => setF({ ...f, key_results: f.key_results.filter((_, j) => j !== i) })} className="text-navy/30 hover:text-destructive"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setF({ ...f, key_results: [...f.key_results, { text: "", target: "", current: 0 }] })} className="text-sm text-violet inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> {t("os.performance.addKr")}</button>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="btn btn-ghost">{t("os.performance.cancel")}</button>
        <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="perf-obj-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.performance.create")}</button>
      </div>
    </Modal>
  );
}

function ObjectivesTab({ meta, isLeader }) {
  const { t } = useI18n();
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = useCallback(async (sc) => {
    setLoading(true);
    try { const r = await api.get("/performance/objectives", { params: { scope: sc } }); setItems(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(scope); }, [scope, load]);
  const updateKr = async (oid, krId, patch) => { try { await api.patch(`/performance/objectives/${oid}/kr/${krId}`, patch); load(scope); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/performance/objectives/${id}`); load(scope); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="perf-objectives">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button onClick={() => setScope("mine")} className={`btn text-sm ${scope === "mine" ? "btn-primary" : "btn-ghost"}`} data-testid="perf-obj-mine">{t("os.performance.scopeMine")}</button>
          {isLeader && <button onClick={() => setScope("all")} className={`btn text-sm ${scope === "all" ? "btn-primary" : "btn-ghost"}`} data-testid="perf-obj-all">{t("os.performance.scopeAll")}</button>}
        </div>
        <button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="perf-new-objective-btn"><Plus className="w-4 h-4" /> {t("os.performance.newObjective")}</button>
      </div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : items.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.performance.noObjectives")}</div>
        : (
          <div className="grid gap-3 lg:grid-cols-2" data-testid="perf-objectives-list">
            {items.map((o) => (
              <div key={o.id} className="card-surface p-4" data-testid={`perf-objective-${o.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><div className="font-medium">{o.title}</div><div className="text-xs text-navy/50">{o.period}{scope === "all" ? ` · ${o.user_name}` : ""}</div></div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${o.status === "completado" ? "badge-emerald" : o.status === "cancelado" ? "badge-danger" : "badge-violet"}`}>{t(`os.performance.objStatus.${o.status}`)}</span>
                    <button onClick={() => del(o.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2"><ProgressBar value={o.progress} /><span className="text-sm text-navy/60 w-10 text-right">{o.progress}%</span></div>
                <div className="mt-3 space-y-2">
                  {(o.key_results || []).map((k) => (
                    <div key={k.id} className="flex items-center gap-2 text-sm" data-testid={`perf-kr-${k.id}`}>
                      <input type="checkbox" checked={!!k.done} onChange={(e) => updateKr(o.id, k.id, { done: e.target.checked })} />
                      <span className={`flex-1 ${k.done ? "line-through text-navy/35" : "text-navy/70"}`}>{k.text}</span>
                      {k.target != null && !k.done && (
                        <input type="number" className="field-sm w-16" defaultValue={k.current} onBlur={(e) => updateKr(o.id, k.id, { current: Number(e.target.value) })} data-testid={`perf-kr-current-${k.id}`} />
                      )}
                      {k.target != null && <span className="text-xs text-navy/40">/ {k.target}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      {open && <ObjectiveDialog meta={meta} isLeader={isLeader} onClose={() => setOpen(false)} onSaved={() => load(scope)} />}
    </div>
  );
}

/* ============ CONTEXTO ============ */
function ContextModal({ userId, onClose }) {
  const { t } = useI18n();
  const [c, setC] = useState(null);
  const [summary, setSummary] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  useEffect(() => { api.get(`/performance/context/${userId}`).then((r) => setC(r.data)).catch(() => {}); }, [userId]);
  const generate = async () => {
    setGenLoading(true);
    try { const r = await api.get(`/performance/summary/${userId}`); setSummary(r.data.summary); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error IA"); } finally { setGenLoading(false); }
  };
  return (
    <Modal title={t("os.performance.context")} onClose={onClose} testid="perf-context-modal" wide>
      {!c ? <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy/40" /></div> : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="card-surface p-3 text-center"><Award className="w-4 h-4 mx-auto text-violet" /><div className="text-2xl font-medium mt-1">{c.recognitions}</div><div className="text-[11px] text-navy/45">{t("os.performance.recognitions")}</div></div>
            <div className="card-surface p-3 text-center"><GraduationCap className="w-4 h-4 mx-auto text-violet" /><div className="text-2xl font-medium mt-1">{c.certificates}</div><div className="text-[11px] text-navy/45">{t("os.performance.certificates")}</div></div>
            <div className="card-surface p-3 text-center"><TrendingUp className="w-4 h-4 mx-auto text-violet" /><div className="text-2xl font-medium mt-1">{c.objectives_avg_progress}%</div><div className="text-[11px] text-navy/45">{t("os.performance.avgProgress")}</div></div>
          </div>
          {c.recent_recognitions?.length > 0 && (
            <div>
              <div className="overline text-navy/40 mb-2">{t("os.performance.recognitions")}</div>
              {c.recent_recognitions.map((r, i) => (
                <div key={i} className="text-sm text-navy/70 py-1"><span className="text-navy/45">{r.badge}</span> · "{r.message}" <span className="text-navy/40">— {r.from_name}</span></div>
              ))}
            </div>
          )}
          <div className="border-t border-navy/10 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="overline text-navy/40 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> {t("os.performance.aiSummary")}</div>
              <button onClick={generate} disabled={genLoading} className="btn btn-primary text-sm" data-testid="perf-generate-summary">
                {genLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("os.performance.generating")}</> : <><Sparkles className="w-4 h-4" /> {t("os.performance.generateSummary")}</>}
              </button>
            </div>
            {summary && <div className="card-surface p-4 text-sm text-navy/75 whitespace-pre-wrap leading-relaxed" data-testid="perf-summary-text">{summary}</div>}
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ============ EVALUACIONES ============ */
function ReviewDialog({ meta, review, onClose, onSaved }) {
  const { t } = useI18n();
  const editing = !!review;
  const [f, setF] = useState({
    user_id: review?.user_id || "", cycle: review?.cycle || meta.cycles[0],
    competencies: review?.competencies?.length ? review.competencies : meta.competencies.map((n) => ({ name: n, score: null })),
    strengths: review?.strengths || "", improvements: review?.improvements || "",
  });
  const [saving, setSaving] = useState(false);
  const setComp = (i, v) => { const n = [...f.competencies]; n[i].score = v === "" ? null : Number(v); setF({ ...f, competencies: n }); };
  const save = async (status) => {
    if (!f.user_id) return toast.error(t("os.performance.employee"));
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/performance/reviews/${review.id}`, { ...f, status });
      } else {
        const created = await api.post("/performance/reviews", f);
        if (status === "enviada") await api.patch(`/performance/reviews/${created.data.id}`, { ...f, status });
      }
      toast.success(t("os.performance.reviewCreated")); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Modal title={t("os.performance.newReview")} onClose={onClose} testid="perf-review-dialog" wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {editing
            ? <input className="field" value={review?.user_name || ""} disabled data-testid="perf-review-user" />
            : <EmployeePicker testid="perf-review-user" value={f.user_id || null} scope="managed"
                placeholder={t("os.performance.employee")}
                onChange={(emp) => setF({ ...f, user_id: emp ? emp.id : "" })} />}
          <select className="field" value={f.cycle} onChange={(e) => setF({ ...f, cycle: e.target.value })}>
            {meta.cycles.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <div className="text-sm font-medium mb-1.5">{t("os.performance.competencies")}</div>
          {f.competencies.map((c, i) => (
            <div key={i} className="flex items-center gap-2 mb-1.5">
              <span className="text-sm text-navy/70 flex-1">{c.name}</span>
              <select className="field-sm w-20" value={c.score ?? ""} onChange={(e) => setComp(i, e.target.value)} data-testid={`perf-comp-${i}`}>
                <option value="">–</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          ))}
        </div>
        <textarea className="field" rows={2} placeholder={t("os.performance.strengths")} value={f.strengths} onChange={(e) => setF({ ...f, strengths: e.target.value })} />
        <textarea className="field" rows={2} placeholder={t("os.performance.improvements")} value={f.improvements} onChange={(e) => setF({ ...f, improvements: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="btn btn-ghost">{t("os.performance.cancel")}</button>
        <button onClick={() => save("borrador")} disabled={saving} className="btn btn-outline" data-testid="perf-review-save">{t("os.performance.save")}</button>
        <button onClick={() => save("enviada")} disabled={saving} className="btn btn-primary" data-testid="perf-review-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.performance.submit")}</button>
      </div>
    </Modal>
  );
}

function ReviewsTab({ meta, isLeader }) {
  const { t } = useI18n();
  const [scope, setScope] = useState(isLeader ? "all" : "mine");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [ctx, setCtx] = useState(null);
  const load = useCallback(async (sc) => {
    setLoading(true);
    try { const r = await api.get("/performance/reviews", { params: { scope: sc } }); setItems(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(scope); }, [scope, load]);
  const del = async (id) => { try { await api.delete(`/performance/reviews/${id}`); load(scope); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="perf-reviews">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {isLeader ? (
          <div className="flex gap-2">
            <button onClick={() => setScope("all")} className={`btn text-sm ${scope === "all" ? "btn-primary" : "btn-ghost"}`}>{t("os.performance.scopeAll")}</button>
            <button onClick={() => setScope("mine")} className={`btn text-sm ${scope === "mine" ? "btn-primary" : "btn-ghost"}`}>{t("os.performance.scopeMine")}</button>
          </div>
        ) : <div className="text-sm text-navy/50">{t("os.performance.scopeMine")}</div>}
        {isLeader && <button onClick={() => setDialog({ new: true })} className="btn btn-primary" data-testid="perf-new-review-btn"><Plus className="w-4 h-4" /> {t("os.performance.newReview")}</button>}
      </div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : items.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.performance.noReviews")}</div>
        : (
          <div className="grid gap-3 lg:grid-cols-2" data-testid="perf-reviews-list">
            {items.map((r) => (
              <div key={r.id} className="card-surface p-4" data-testid={`perf-review-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-medium">{r.user_name} · {r.cycle}</div><div className="text-xs text-navy/50">{t("os.performance.employee")}: {r.reviewer_name}</div></div>
                  <div className="flex items-center gap-2">
                    {r.overall_score != null && <span className="flex items-center gap-1 text-gold text-sm"><Star className="w-3.5 h-3.5 fill-current" />{r.overall_score}</span>}
                    <span className={`badge ${r.status === "enviada" ? "badge-emerald" : "badge-muted"}`}>{r.status === "enviada" ? t("os.performance.sent") : t("os.performance.draft")}</span>
                  </div>
                </div>
                {r.strengths && <p className="text-sm text-navy/60 mt-2"><b className="text-navy/45">{t("os.performance.strengths")}:</b> {r.strengths}</p>}
                {isLeader && (
                  <div className="mt-3 flex items-center gap-2">
                    <button onClick={() => setDialog({ review: r })} className="btn btn-ghost text-sm" data-testid={`perf-review-edit-${r.id}`}>{t("os.performance.edit")}</button>
                    <button onClick={() => setCtx(r.user_id)} className="btn btn-ghost text-sm">{t("os.performance.viewContext")}</button>
                    <button onClick={() => del(r.id)} className="text-navy/30 hover:text-destructive ml-auto"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      {dialog && <ReviewDialog meta={meta} review={dialog.review} onClose={() => setDialog(null)} onSaved={() => load(scope)} />}
      {ctx && <ContextModal userId={ctx} onClose={() => setCtx(null)} />}
    </div>
  );
}

/* ============ 1:1s ============ */
function OneOnOnesTab({ meta }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ report_id: "", date: "", agenda: "" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/performance/oneonones"); setItems(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!f.report_id || !f.date) return toast.error(t("os.performance.date"));
    setSaving(true);
    try { await api.post("/performance/oneonones", f); toast.success(t("os.performance.oneononeCreated")); setOpen(false); setF({ report_id: "", date: "", agenda: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const saveNotes = async (id, notes) => { try { await api.patch(`/performance/oneonones/${id}`, { notes }); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const complete = async (id) => { try { await api.patch(`/performance/oneonones/${id}`, { status: "completada" }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/performance/oneonones/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="perf-oneonones">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="perf-new-oneonone-btn"><Plus className="w-4 h-4" /> {t("os.performance.newOneOnOne")}</button></div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : items.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.performance.noOneOnOnes")}</div>
        : (
          <div className="grid gap-3 lg:grid-cols-2" data-testid="perf-oneonones-list">
            {items.map((m) => (
              <div key={m.id} className="card-surface p-4" data-testid={`perf-oneonone-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-medium">{m.manager_name} ↔ {m.report_name}</div><div className="text-xs text-navy/50">{m.date?.slice(0, 10)}</div></div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${m.status === "completada" ? "badge-emerald" : "badge-violet"}`}>{m.status === "completada" ? t("os.performance.completed") : t("os.performance.scheduled")}</span>
                    <button onClick={() => del(m.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {m.agenda && <p className="text-sm text-navy/60 mt-2"><b className="text-navy/45">{t("os.performance.agenda")}:</b> {m.agenda}</p>}
                <textarea className="field mt-2 text-sm" rows={2} placeholder={t("os.performance.notes")} defaultValue={m.notes} onBlur={(e) => saveNotes(m.id, e.target.value)} data-testid={`perf-oneonone-notes-${m.id}`} />
                {m.status !== "completada" && <button onClick={() => complete(m.id)} className="btn btn-ghost text-sm mt-2"><Check className="w-4 h-4" /> {t("os.performance.markComplete")}</button>}
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.performance.newOneOnOne")} onClose={() => setOpen(false)} testid="perf-oneonone-dialog">
          <div className="space-y-3">
            <EmployeePicker testid="perf-oneonone-with" value={f.report_id || null} scope="managed"
              placeholder={t("os.performance.withWho")}
              onChange={(emp) => setF({ ...f, report_id: emp ? emp.id : "" })} />
            <DatePicker testid="perf-oneonone-date" value={f.date} onChange={(v) => setF({ ...f, date: v })} />
            <textarea className="field" rows={3} placeholder={t("os.performance.agenda")} value={f.agenda} onChange={(e) => setF({ ...f, agenda: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.performance.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="perf-oneonone-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.performance.create")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ FEEDBACK ============ */
function FeedbackTab({ meta }) {
  const { t } = useI18n();
  const [scope, setScope] = useState("received");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ to_id: "", type: "general", message: "", shared: false });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async (sc) => {
    setLoading(true);
    try { const r = await api.get("/performance/feedback", { params: { scope: sc } }); setItems(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(scope); }, [scope, load]);
  const submit = async () => {
    if (!f.to_id || !f.message.trim()) return toast.error(t("os.performance.message"));
    setSaving(true);
    try { await api.post("/performance/feedback", f); toast.success(t("os.performance.feedbackSent")); setOpen(false); setF({ to_id: "", type: "general", message: "", shared: false }); load(scope); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5" data-testid="perf-feedback">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button onClick={() => setScope("received")} className={`btn text-sm ${scope === "received" ? "btn-primary" : "btn-ghost"}`}>{t("os.performance.scopeReceived")}</button>
          <button onClick={() => setScope("given")} className={`btn text-sm ${scope === "given" ? "btn-primary" : "btn-ghost"}`}>{t("os.performance.scopeGiven")}</button>
        </div>
        <button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="perf-give-feedback-btn"><Plus className="w-4 h-4" /> {t("os.performance.giveFeedback")}</button>
      </div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : items.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.performance.noFeedback")}</div>
        : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="perf-feedback-list">
            {items.map((fb) => (
              <div key={fb.id} className="card-surface p-4" data-testid={`perf-feedback-${fb.id}`}>
                <div className="flex items-center justify-between">
                  <span className="badge badge-violet">{t(`os.performance.fbType.${fb.type}`)}</span>
                  <span className="text-xs text-navy/40">{fb.from_name} → {fb.to_name}</span>
                </div>
                <p className="text-sm text-navy/70 mt-2">"{fb.message}"</p>
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.performance.giveFeedback")} onClose={() => setOpen(false)} testid="perf-feedback-dialog">
          <div className="space-y-3">
            <EmployeePicker testid="perf-feedback-to" value={f.to_id || null}
              placeholder={t("os.performance.to")}
              onChange={(emp) => setF({ ...f, to_id: emp ? emp.id : "" })} />
            <select className="field" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
              {meta.feedback_types.map((x) => <option key={x} value={x}>{t(`os.performance.fbType.${x}`)}</option>)}
            </select>
            <textarea className="field" rows={3} placeholder={t("os.performance.message")} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="perf-feedback-message" />
            <label className="flex items-center gap-2 text-sm text-navy/60"><input type="checkbox" checked={f.shared} onChange={(e) => setF({ ...f, shared: e.target.checked })} /> {t("os.performance.shared")}</label>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.performance.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="perf-feedback-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.performance.send")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ STUDIO ============ */
export default function PerformanceStudio({ embedded = false }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isLeader = LEADERSHIP.includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState(isLeader ? "dashboard" : "objectives");
  useEffect(() => { api.get("/performance/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const allTabs = [
    ...(isLeader ? [{ key: "dashboard", label: t("os.performance.tabs.dashboard"), icon: LayoutDashboard }] : []),
    { key: "objectives", label: t("os.performance.tabs.objectives"), icon: Target },
    { key: "reviews", label: t("os.performance.tabs.reviews"), icon: ClipboardCheck },
    { key: "oneonones", label: t("os.performance.tabs.oneonones"), icon: MessagesSquare },
    { key: "feedback", label: t("os.performance.tabs.feedback"), icon: MessageSquarePlus },
  ];

  const Shell = ({ children }) => embedded ? <>{children}</> : <OSLayout title={t("os.performance.title")}>{children}</OSLayout>;

  return (
    <Shell>
      <div className="space-y-8" data-testid="performance-page">
        {!embedded && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Target className="w-3.5 h-3.5" /> {t("os.performance.overline")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.performance.title")}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.performance.subtitle")}</p>
        </motion.div>
        )}

        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-1">
          {allTabs.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`perf-tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition-colors ${tab === tb.key ? "bg-gradient-violet text-white" : "text-navy/55 hover:text-navy hover:bg-navy/5"}`}>
              <tb.icon className="w-3.5 h-3.5" /> {tb.label}
            </button>
          ))}
        </div>

        {!meta ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "dashboard" && isLeader && <DashboardTab />}
            {tab === "objectives" && <ObjectivesTab meta={meta} isLeader={isLeader} />}
            {tab === "reviews" && <ReviewsTab meta={meta} isLeader={isLeader} />}
            {tab === "oneonones" && <OneOnOnesTab meta={meta} />}
            {tab === "feedback" && <FeedbackTab meta={meta} />}
          </>
        )}
      </div>
    </Shell>
  );
}
